import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import ProfileCard from "@/components/ProfileCard";
import { publicApi } from "@/lib/account";
import useSeo from "@/lib/seo";

const PAGE = 24;

const Select = ({ id, value, onChange, children }) => (
  <select data-testid={`filter-${id}-select`} value={value} onChange={(e) => onChange(e.target.value)} className="field text-sm">{children}</select>
);

export default function Browse() {
  const [f, setF] = useState({ gender: "", religion: "", country: "", kind: "", q: "", min_age: 18, max_age: 80, international: "" });
  const [meta, setMeta] = useState({ countries: [], religions: [] });
  useEffect(() => { publicApi.get("/meta").then((r) => setMeta(r.data)); }, []);
  const [data, setData] = useState({ items: [], total: 0 });
  const [skip, setSkip] = useState(0);
  useSeo("Browse Brides & Grooms Worldwide", "Search brides and grooms from 25+ countries by country, faith and age. International and inter-cultural marriages welcome.");
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => { setSkip(0); setF((s) => ({ ...s, [k]: v })); };

  useEffect(() => {
    const params = Object.fromEntries(Object.entries({ ...f, skip, limit: PAGE }).filter(([, v]) => v !== ""));
    setLoading(true);
    const t = setTimeout(() => publicApi.get("/profiles", { params }).then((r) => {
      setData((d) => ({ total: r.data.total, items: skip ? [...d.items, ...r.data.items] : r.data.items }));
    }).finally(() => setLoading(false)), 250);
    return () => clearTimeout(t);
  }, [f, skip]);

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="browse-page">
      <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Discover</p>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3">Browse profiles</h1>
      <div className="card-royal rounded-2xl p-5 mt-10 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="browse-filters">
        <div className="col-span-2 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="filter-search-input" placeholder="Name, profession, city" value={f.q} onChange={(e) => set("q")(e.target.value)} className="field text-sm" style={{ paddingLeft: "2.25rem" }} />
        </div>
        <Select id="gender" value={f.gender} onChange={set("gender")}><option value="">Bride or groom</option><option value="Female">Brides</option><option value="Male">Grooms</option></Select>
        <Select id="religion" value={f.religion} onChange={set("religion")}><option value="">Any religion</option>{meta.religions.map((r) => <option key={r}>{r}</option>)}</Select>
        <Select id="country" value={f.country} onChange={set("country")}><option value="">Any country</option>{meta.countries.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}</Select>
        <Select id="kind" value={f.kind} onChange={set("kind")}><option value="">All profiles</option><option value="real">Members only</option><option value="ai">AI profiles</option></Select>
        <div className="col-span-2 md:col-span-6 flex items-center gap-3 text-sm text-slate-400">
          <span>Age</span>
          <input type="number" data-testid="filter-min-age" min={18} max={80} value={f.min_age} onChange={(e) => set("min_age")(Number(e.target.value) || 18)} className="field w-20 text-sm" />
          <span>to</span>
          <input type="number" data-testid="filter-max-age" min={18} max={80} value={f.max_age} onChange={(e) => set("max_age")(Number(e.target.value) || 80)} className="field w-20 text-sm" />
          <label className="flex items-center gap-2 ml-4 cursor-pointer"><input type="checkbox" checked={!!f.international} onChange={(e) => set("international")(e.target.checked ? "true" : "")} className="accent-amber-500" data-testid="filter-international-checkbox" /> Open to international match</label>
          <span className="ml-auto" data-testid="browse-total-count">{data.total} profiles</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-10">
        {data.items.map((p, i) => <ProfileCard key={p.id} p={p} index={i} />)}
      </div>
      {!loading && data.items.length === 0 && <p className="text-center text-slate-400 py-20" data-testid="browse-empty">No profiles match these filters.</p>}
      {data.items.length < data.total && (
        <div className="text-center mt-12">
          <button data-testid="browse-load-more" disabled={loading} onClick={() => setSkip(data.items.length)} className="btn-ghost px-8 py-3 rounded-full">
            {loading ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
